import { compact, uniq } from "lodash";
import type { MarketingAction } from "src/types/action/MarketingAction";
import { MarketingActionType } from "src/types/enum/MarketingActionType";
import type { OfferScenario } from "src/types/offer/OfferScenario";
import type {
    SponsoredAdScenario,
    SponsoredAdScenarioPerformance,
} from "src/types/sponsored-ads/SponsoredAdScenario";
import { MarketingActionActivationTypeEnum } from "src/utils/activationTypes";

export enum MarketingActionVariantEnum {
    Classic = "classic",
    Offer = "offer",
    SponsoredAd = "sponsored-ad",
    Duo = "duo",
    AutoRule = "auto-rule",
    Special = "special",
    CoFunded = "co-funded",
    SmartScheduler = "smart-scheduler",
    OptimizedAutoRule = "optimized-auto-rule",
}

export function isMarketingActionAnAutoRule(
    marketingAction: Pick<MarketingAction, "activationTypes">
): boolean {
    return marketingAction.activationTypes.includes(MarketingActionActivationTypeEnum.AutoRule);
}

export function isMarketingActionADuo(
    marketingAction: Pick<MarketingAction, "duoMarketingAction">
): boolean {
    return (
        marketingAction.duoMarketingAction !== undefined &&
        marketingAction.duoMarketingAction !== null
    );
}

export function isMarketingActionASponsoredAd(marketingAction: {
    scenarios: Partial<SponsoredAdScenario>[];
}): boolean {
    return compact(marketingAction.scenarios).some(
        (scenario) => scenario.bid !== undefined && scenario.bid !== null
    );
}

export function isMarketingActionASponsoredAdPerformance(marketingAction: {
    scenarios: Partial<SponsoredAdScenarioPerformance>[];
}): boolean {
    return compact(marketingAction.scenarios).some(
        (scenario) => scenario.bidAndBudgetList !== undefined && scenario.bidAndBudgetList !== null
    );
}

export function isMarketingActionAnOffer(marketingAction: {
    scenarios: Partial<OfferScenario>[];
}): boolean {
    return compact(marketingAction.scenarios).some(
        (scenario) => scenario.type !== undefined && scenario.type !== null
    );
}

export function isMarketingActionASpecialOffer(
    marketingAction: Pick<MarketingAction, "activationTypes">
): boolean {
    return marketingAction.activationTypes.includes(MarketingActionActivationTypeEnum.Special);
}

export function isMarketingActionACoFundedOffer(
    marketingAction: Pick<MarketingAction, "activationTypes">
): boolean {
    return marketingAction.activationTypes.includes(MarketingActionActivationTypeEnum.CoFunded);
}

// TODO: a retirer une fois le variant a retirer une fois le variant reçu du back
export function isMarketingActionASmartSchedulerOffer(
    marketingAction: Pick<MarketingAction, "activationTypes">
): boolean {
    return marketingAction.activationTypes.includes(
        MarketingActionActivationTypeEnum.SmartScheduler
    );
}

export function isMarketingActionAnOptimizedAutoRule(
    marketingAction: Pick<MarketingAction, "activationTypes">
): boolean {
    return marketingAction.activationTypes.includes(
        MarketingActionActivationTypeEnum.OptimizedAutoRule
    );
}

export const TEMPLATE_IGNORE_VARIANTS_LIST = [
    MarketingActionVariantEnum.CoFunded,
    MarketingActionVariantEnum.Special,
    MarketingActionVariantEnum.OptimizedAutoRule,
];

type ScenarioVariant = Partial<SponsoredAdScenarioPerformance> &
    Partial<OfferScenario> &
    Partial<SponsoredAdScenario>;
/**
 * Get the list of variants for a specific Marketing Action
 * @param marketingAction Marketing action to handle
 * @returns List of variants
 */
export function getMarketingActionVariants(
    marketingAction: Pick<MarketingAction, "activationTypes" | "duoMarketingAction"> & {
        scenarios: ScenarioVariant[];
    },
    { ignoreVariantList = [] }: { ignoreVariantList?: MarketingActionVariantEnum[] } = {}
): MarketingActionVariantEnum[] {
    const variantList = uniq(
        [
            isMarketingActionAnAutoRule(marketingAction) && MarketingActionVariantEnum.AutoRule,
            isMarketingActionADuo(marketingAction) && MarketingActionVariantEnum.Duo,
            isMarketingActionASponsoredAd(marketingAction) &&
                MarketingActionVariantEnum.SponsoredAd,
            isMarketingActionASponsoredAdPerformance(marketingAction) &&
                MarketingActionVariantEnum.SponsoredAd,
            isMarketingActionAnOffer(marketingAction) && MarketingActionVariantEnum.Offer,
            isMarketingActionASpecialOffer(marketingAction) && MarketingActionVariantEnum.Special,
            isMarketingActionACoFundedOffer(marketingAction) && MarketingActionVariantEnum.CoFunded,
            isMarketingActionASmartSchedulerOffer(marketingAction) &&
                MarketingActionVariantEnum.SmartScheduler,
            isMarketingActionAnOptimizedAutoRule(marketingAction) &&
                MarketingActionVariantEnum.OptimizedAutoRule,
            marketingAction.activationTypes.includes(MarketingActionActivationTypeEnum.Classic) &&
                MarketingActionVariantEnum.Classic,
        ].filter(Boolean) as MarketingActionVariantEnum[]
    );

    if (ignoreVariantList.length > 0) {
        return variantList.filter((variant) => !ignoreVariantList.includes(variant));
    }
    return variantList;
}

/**
 * Get the main MarketingActionType from a list of variants
 * @param variants List of variants
 * @returns MarketingActionType
 */
export function getMarketingActionTypeFromVariants(
    variants: MarketingActionVariantEnum[]
): MarketingActionType {
    if (
        variants.includes(MarketingActionVariantEnum.Duo) ||
        (variants.includes(MarketingActionVariantEnum.Offer) &&
            variants.includes(MarketingActionVariantEnum.SponsoredAd))
    ) {
        return MarketingActionType.Duo;
    }
    if (variants.includes(MarketingActionVariantEnum.Offer)) {
        return MarketingActionType.Offer;
    }
    if (variants.includes(MarketingActionVariantEnum.SponsoredAd)) {
        return MarketingActionType.SponsoredAd;
    }
    throw new Error(`Impossible variant combination ${JSON.stringify(variants)}`);
}

/**
 * Get the main MarketingActionType from a list of variants
 * @param variants List of variants
 * @returns MarketingActionType.Offer | MarketingActionType.SponsoredAd
 */
export function getSimpleMarketingActionTypeFromVariants(
    variants: MarketingActionVariantEnum[]
): MarketingActionType {
    if (variants.includes(MarketingActionVariantEnum.Offer)) {
        return MarketingActionType.Offer;
    }
    if (variants.includes(MarketingActionVariantEnum.SponsoredAd)) {
        return MarketingActionType.SponsoredAd;
    }
    throw new Error(`Impossible variant combination ${JSON.stringify(variants)}`);
}

export function getConceptVariantFromVariantList(
    variantList: MarketingActionVariantEnum[],
    {
        withAutoRule = false,
        withSpecial = false,
    }: { withAutoRule?: boolean; withSpecial?: boolean } = {}
): MarketingActionVariantEnum {
    if (variantList.includes(MarketingActionVariantEnum.Duo)) {
        return MarketingActionVariantEnum.Duo;
    } else if (variantList.includes(MarketingActionVariantEnum.SmartScheduler)) {
        return MarketingActionVariantEnum.SmartScheduler;
    } else if (withAutoRule && variantList.includes(MarketingActionVariantEnum.AutoRule)) {
        return MarketingActionVariantEnum.AutoRule;
    } else if (withAutoRule && variantList.includes(MarketingActionVariantEnum.OptimizedAutoRule)) {
        return MarketingActionVariantEnum.OptimizedAutoRule;
    } else if (withSpecial && variantList.includes(MarketingActionVariantEnum.Special)) {
        return MarketingActionVariantEnum.Special;
    } else if (variantList.includes(MarketingActionVariantEnum.Offer)) {
        return MarketingActionVariantEnum.Offer;
    } else if (variantList.includes(MarketingActionVariantEnum.SponsoredAd)) {
        return MarketingActionVariantEnum.SponsoredAd;
    } else {
        return MarketingActionVariantEnum.Offer;
    }
}
